/**
=========================================================
* Otis Admin PRO - v2.0.1
=========================================================

* Product Page: https://material-ui.com/store/items/otis-admin-pro-material-dashboard-react/
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

import { initializeApp } from "firebase/app";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Otis Admin PRO React components
import MDBox from "components/MDBox";

// Otis Admin PRO React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Otis Admin PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Otis Admin PRO React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider, css, Global } from "@emotion/react";
import createCache from "@emotion/cache";

// Otis Admin PRO React routes
// eslint-disable-next-line import/no-named-as-default-member
import routes, { privateRoute } from "routes";

// Otis Admin PRO React contexts
import { useMaterialUIController } from "utils/hooks";

// Images
import brandWhite from "assets/images/logo_for_dealdashboard-1.jpeg";
import brandDark from "assets/images/logo_for_dealdashboard.jpeg";
import { useFlatInject } from "store";
import { getToken, getUserId, getData, getIsVerify, getUserType } from "utils/auth";
import { OBJECT_URL_IGNORE_REDIRECT } from "constants/urls/urls";
import Message from "./components/Message";
import Loading from "./components/Loading";
// import LandingPage from "./pages/landing-page";
// import ResendEmail from "pages/resend-email";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_APIKEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTHDOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECTID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGINGSENDERID,
  appId: process.env.REACT_APP_FIREBASE_APPID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENTID,
};

export default function App() {
  const [controller, { setMiniSidenav, setOpenConfigurator }] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const token = getToken();
  const userId = getUserId();
  const userState = getData();
  const isVerified = getIsVerify();
  const userType = getUserType();
  const [{ user: info }] = useFlatInject("user");
  const [user] = useFlatInject("user");

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(!openConfigurator);

  useEffect(() => {
    initializeApp(firebaseConfig);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (hash === "#logout") {
      user.logout().then(() => {
        localStorage.clear();
        navigate("/sign-in");
      });
    }
  }, [hash]);

  const selectPage = (route) => {
    if (
      ((info?.isVerified || isVerified === "true") && userType === "EMAIL") ||
      OBJECT_URL_IGNORE_REDIRECT[pathname] ||
      ["GOOGLE", "FACEBOOK"].includes(userType)
    ) {
      return route.component;
    }
    return <Navigate to="/resend-email" />;
  };

  const getRoutes = (allRoutes) =>
    allRoutes
      .filter((route) => (token && userId ? route : route !== "/deal-planner"))
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          if (!token && !userId) {
            return <Route exact path={route.route} element={route.component} key={route.key} />;
          }
          return <Route exact path={route.route} element={selectPage(route)} key={route.key} />;
        }

        return null;
      });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <Message />
        <Loading />
        {layout === "dashboard" && token && userId && (
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Deal Dashboard"
            routes={token && userId ? privateRoute : routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        )}
        <Routes>
          {getRoutes(token && userId ? privateRoute : routes)}
          <Route
            path="*"
            element={
              token && userId ? <Navigate to="/deal-planner" /> : <Navigate to="/landing-page" />
            }
          />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <Global
        styles={{
          "html,body,#root": {
            height: "100%",
          },
          "@media print": {
            ".MuiDrawer-docked,header": {
              display: "none",
            },
            ".no-print": {
              display: "none !important",
            },
          },
        }}
      />
      <CssBaseline />
      <Message />
      <Loading />
      {layout === "dashboard" && token && userId && (
        <Sidenav
          color={sidenavColor}
          brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
          brandName="Deal Dashboard"
          routes={token && userId ? privateRoute : routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      <Routes>
        {getRoutes(token && userId ? privateRoute : routes)}
        <Route
          path="*"
          element={
            token && userId ? <Navigate to="/deal-planner" /> : <Navigate to="/landing-page" />
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
